/* TECHWEAR STORM - CUSTOM BACK IN STOCK
 *
 * Follows Prestige's own <variant-picker> (its `selectedVariant` getter + the "variant:change" event dispatched on the
 * product form). No parallel variant logic, no framework.
 *
 * On submit:
 *   1. Klaviyo client API  -> native Back in Stock subscription for the exact Shopify variant (public key only)
 *   2. Serverless endpoint -> profile properties + "Back in Stock - Techwear Storm" list (+ newsletter only if opted in)
 * Success depends on (1). (2) is best effort and never blocks the shopper.
 */
(() => {
  if (window.customElements.get('techwear-back-in-stock')) return;

  const KLAVIYO_REVISION = '2026-07-15';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const MESSAGES = {
    first_name: 'Please enter your first name.',
    email: 'Please enter a valid email address.',
    api: 'Something went wrong. Please try again.',
    submitting: 'Adding...',
  };

  function track(name, payload) {
    try {
      window.Shopify?.analytics?.publish?.(name, payload);
      if (typeof window.gtag === 'function') window.gtag('event', name, payload);
      if (typeof window.fbq === 'function') window.fbq('trackCustom', name, payload);
    } catch (e) {
      /* analytics must never break the form */
    }
  }

  class TechwearBackInStock extends HTMLElement {
    #abort = new AbortController();
    #data = {};
    #pending = false;
    #submitting = false;
    #viewedVariants = new Set();

    connectedCallback() {
      try {
        this.#data = JSON.parse(this.querySelector('script[data-bis-product]')?.textContent || '{}');
      } catch (error) {
        console.error('[Techwear BIS] invalid product data, form disabled', error);
        this.hidden = true;
        return;
      }
      this.form = this.querySelector('[data-bis-form]');
      this.submitButton = this.querySelector('[data-bis-submit]');
      this.submitLabel = this.submitButton.textContent;

      const signal = this.#abort.signal;
      const formId = this.getAttribute('form-id');
      const productForm = document.forms[formId];

      // The theme's variant-picker fires this after it has resolved the new variant
      productForm?.addEventListener('variant:change', (event) => this.#onVariantChange(event.detail?.variant), { signal });

      // Between an option click and "variant:change" the picker is still fetching the new combination.
      // Block submission during that window so a stale variant can never be subscribed.
      // Capture phase: must run BEFORE the theme's delegate, which can resolve a preloaded variant synchronously.
      document.addEventListener('change', (event) => {
        if (event.target.matches?.(`input[data-option-position][form="${formId}"]`)) {
          this.#pending = true;
          this.submitButton.disabled = true;
        }
      }, { signal, capture: true });

      this.form.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
      this.form.addEventListener('input', (event) => this.#clearError(event.target.name), { signal });

      // Only count a view when actually rendered (the theme also mounts a closed quick-buy drawer copy)
      if (!this.hidden && this.getClientRects().length) this.#trackView(this.#currentVariant());
    }

    disconnectedCallback() {
      this.#abort.abort();
    }

    /** The variant selected RIGHT NOW, read from the theme itself. */
    #currentVariant() {
      const formId = this.getAttribute('form-id');
      const picker = document.querySelector(`variant-picker[form-id="${formId}"]`);
      let id = picker?.selectedVariant?.id;

      if (!id) {
        const input = document.forms[formId]?.querySelector('input[name="id"]');
        id = input?.value;
      }

      if (!id) return null;
      const known = this.#data.variants?.[id];
      const fromPicker = picker?.selectedVariant?.id == id ? picker.selectedVariant : null;

      return {
        id: String(id),
        title: fromPicker?.title ?? known?.title ?? '',
        available: fromPicker ? fromPicker.available : known?.available,
      };
    }

    #label(variant) {
      if (this.#data.single_variant || !variant?.title || variant.title === 'Default Title') {
        return this.#data.product_title;
      }
      return variant.title;
    }

    #onVariantChange(variant) {
      this.#pending = false;
      this.submitButton.disabled = this.#submitting;

      if (!variant || variant.available) {
        this.hidden = true;
        return;
      }

      this.querySelector('[data-bis-variant-label]').textContent = this.#label(variant);

      // New sold-out variant: offer the form again (keep what the shopper already typed)
      this.querySelector('[data-bis-panel]').hidden = false;
      this.querySelector('[data-bis-success]').hidden = true;
      this.hidden = false;
      this.#trackView(variant);
    }

    #trackView(variant) {
      if (!variant || this.#viewedVariants.has(String(variant.id))) return;
      this.#viewedVariants.add(String(variant.id));
      track('bis_form_view', this.#analyticsPayload(variant));
    }

    #analyticsPayload(variant) {
      return {
        product_id: String(this.#data.product_id),
        variant_id: String(variant?.id ?? ''),
        product_handle: this.#data.product_handle,
        variant_title: variant?.title ?? '',
      };
    }

    #setError(name, message) {
      const error = this.querySelector(`[data-bis-error="${name}"]`);
      const field = this.form.elements[name];
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
      field?.setAttribute('aria-invalid', 'true');
    }

    #clearError(name) {
      const error = this.querySelector(`[data-bis-error="${name}"]`);
      if (error) error.hidden = true;
      this.form.elements[name]?.removeAttribute('aria-invalid');
      this.#clearFormError();
    }

    #clearFormError() {
      const error = this.querySelector('[data-bis-error="form"]');
      if (error) error.hidden = true;
    }

    #setBusy(busy) {
      this.#submitting = busy;
      this.submitButton.disabled = busy || this.#pending;
      this.submitButton.setAttribute('aria-busy', String(busy));
      this.submitButton.textContent = busy ? MESSAGES.submitting : this.submitLabel;
    }

    async #onSubmit(event) {
      event.preventDefault();
      if (this.#submitting || this.#pending) return;

      const firstName = this.form.elements.first_name.value.trim();
      const email = this.form.elements.email.value.trim();
      const optIn = Boolean(this.form.elements.marketing_opt_in?.checked);
      const honeypot = this.form.elements.company?.value;

      this.#clearError('first_name');
      this.#clearError('email');

      let firstInvalid = null;
      if (!firstName) {
        this.#setError('first_name', MESSAGES.first_name);
        firstInvalid ??= this.form.elements.first_name;
      }
      if (!EMAIL_RE.test(email)) {
        this.#setError('email', MESSAGES.email);
        firstInvalid ??= this.form.elements.email;
      }
      if (firstInvalid) {
        firstInvalid.focus();
        return;
      }

      // Read the variant at submission time, never a cached one
      const variant = this.#currentVariant();
      if (!variant || variant.available) return;

      const analytics = this.#analyticsPayload(variant);
      track('bis_form_submit', analytics);

      if (honeypot) {
        this.#showSuccess(variant); // silently drop bots
        return;
      }

      this.#setBusy(true);

      try {
        const [klaviyoResult, serverResult] = await Promise.allSettled([
          this.#subscribeKlaviyo(email, variant),
          this.#notifyEndpoint({ firstName, email, optIn, variant }),
        ]);

        if (serverResult.status === 'rejected') {
          console.warn('[Techwear BIS] list/profile sync failed', serverResult.reason);
        }

        if (klaviyoResult.status === 'rejected') {
          throw klaviyoResult.reason;
        }

        track('bis_form_success', analytics);
        this.#showSuccess(variant);
      } catch (error) {
        console.error('[Techwear BIS]', error);
        track('bis_form_error', { ...analytics, error: String(error?.message || error).slice(0, 100) });
        this.#setError('form', MESSAGES.api);
      } finally {
        this.#setBusy(false);
      }
    }

    async #subscribeKlaviyo(email, variant) {
      const publicKey = this.getAttribute('public-key');
      const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(publicKey)}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email } } },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variant.id}` } },
            },
          },
        }),
      });

      // Klaviyo answers 202 for new AND duplicate subscriptions: both are a success for the shopper
      if (!response.ok) {
        throw new Error(`Klaviyo ${response.status}`);
      }
    }

    async #notifyEndpoint({ firstName, email, optIn, variant }) {
      const endpoint = this.getAttribute('endpoint');
      if (!endpoint) return;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName,
          email,
          marketing_opt_in: optIn,
          product_id: String(this.#data.product_id),
          product_handle: this.#data.product_handle,
          product_title: this.#data.product_title,
          product_url: this.#data.product_url,
          featured_image: this.#data.featured_image,
          variant_id: variant.id,
          variant_title: this.#label(variant),
        }),
        keepalive: true,
      });

      if (!response.ok) throw new Error(`Endpoint ${response.status}`);
    }

    #showSuccess(variant) {
      this.querySelector('[data-bis-success-label]').textContent = this.#label(variant);
      this.querySelector('[data-bis-panel]').hidden = true;
      const success = this.querySelector('[data-bis-success]');
      success.hidden = false;
      success.focus({ preventScroll: true });
    }
  }

  window.customElements.define('techwear-back-in-stock', TechwearBackInStock);
})();
